/**
 * Dev-only overlay shown while WasmAPI is parked after an unrecovered panic.
 *
 * A wasm panic with no unwind leaves the instance unusable, so the canvas sits
 * frozen on its last good frame until we recreate it. Without anything on top
 * that reads as intentional, this puts a beetle on the canvas instead: there is
 * a bug.
 *
 * Plain DOM rather than React because WasmAPI owns the parked state and is not a
 * component. Keeping it self-contained also means deleting this file is the only
 * step needed to remove the whole thing.
 */

const ELEMENT_ID = 'weavy-dev-panic-overlay';
const STYLE_ID = 'weavy-dev-panic-overlay-styles';

const STYLES = `
#${ELEMENT_ID} {
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  background: radial-gradient(circle at center, rgba(12, 10, 16, 0.62), rgba(12, 10, 16, 0.86));
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  animation: weavy-beetle-fade-in 180ms ease-out;
}

#${ELEMENT_ID} .weavy-beetle-title {
  color: #ff8ad0;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

#${ELEMENT_ID} .weavy-beetle-subtitle {
  color: rgba(255, 255, 255, 0.62);
  font-size: 12.5px;
}

#${ELEMENT_ID} svg {
  width: 168px;
  height: 168px;
  overflow: visible;
}

#${ELEMENT_ID} .weavy-beetle-body {
  transform-box: view-box;
  transform-origin: 60px 70px;
  animation: weavy-beetle-dance 900ms ease-in-out infinite;
}

#${ELEMENT_ID} .weavy-beetle-legs {
  transform-box: view-box;
  transform-origin: 60px 72px;
  animation: weavy-beetle-legs 450ms ease-in-out infinite alternate;
}

#${ELEMENT_ID} .weavy-beetle-legs-right {
  animation-direction: alternate-reverse;
}

#${ELEMENT_ID} .weavy-beetle-antennae {
  transform-box: view-box;
  transform-origin: 60px 36px;
  animation: weavy-beetle-antennae 700ms ease-in-out infinite alternate;
}

#${ELEMENT_ID} .weavy-beetle-shadow {
  transform-box: view-box;
  transform-origin: 60px 106px;
  animation: weavy-beetle-shadow 900ms ease-in-out infinite;
}

@keyframes weavy-beetle-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes weavy-beetle-dance {
  0%   { transform: translateY(0) rotate(-5deg); }
  25%  { transform: translateY(-9px) rotate(0deg); }
  50%  { transform: translateY(0) rotate(5deg); }
  75%  { transform: translateY(-9px) rotate(0deg); }
  100% { transform: translateY(0) rotate(-5deg); }
}

@keyframes weavy-beetle-legs {
  from { transform: rotate(-13deg); }
  to   { transform: rotate(13deg); }
}

@keyframes weavy-beetle-antennae {
  from { transform: rotate(-10deg); }
  to   { transform: rotate(10deg); }
}

@keyframes weavy-beetle-shadow {
  0%, 100% { transform: scaleX(1); opacity: 0.4; }
  25%, 75% { transform: scaleX(0.78); opacity: 0.22; }
}

@media (prefers-reduced-motion: reduce) {
  #${ELEMENT_ID} .weavy-beetle-body,
  #${ELEMENT_ID} .weavy-beetle-legs,
  #${ELEMENT_ID} .weavy-beetle-antennae,
  #${ELEMENT_ID} .weavy-beetle-shadow {
    animation: none;
  }
}
`;

const MARKUP = `
<svg viewBox="0 0 120 120" aria-hidden="true">
  <ellipse class="weavy-beetle-shadow" cx="60" cy="106" rx="27" ry="5" fill="#000" />
  <g class="weavy-beetle-body" fill="none" stroke="#2a1a2f" stroke-width="4" stroke-linecap="round">
    <g class="weavy-beetle-legs weavy-beetle-legs-left">
      <path d="M41 58 L19 45" />
      <path d="M39 71 L15 72" />
      <path d="M42 83 L21 97" />
    </g>
    <g class="weavy-beetle-legs weavy-beetle-legs-right">
      <path d="M79 58 L101 45" />
      <path d="M81 71 L105 72" />
      <path d="M78 83 L99 97" />
    </g>
    <g class="weavy-beetle-antennae">
      <path d="M52 30 L44 16" />
      <path d="M68 30 L76 16" />
      <circle cx="43" cy="14" r="3" fill="#2a1a2f" />
      <circle cx="77" cy="14" r="3" fill="#2a1a2f" />
    </g>
    <circle cx="60" cy="37" r="14" fill="#2a1a2f" stroke="none" />
    <circle cx="54" cy="34" r="2.6" fill="#ff8ad0" stroke="none" />
    <circle cx="66" cy="34" r="2.6" fill="#ff8ad0" stroke="none" />
    <ellipse cx="60" cy="73" rx="25" ry="31" fill="#ff2fa4" stroke="#2a1a2f" stroke-width="4" />
    <path d="M60 45 L60 103" stroke="#2a1a2f" stroke-width="4" />
    <circle cx="47" cy="65" r="5" fill="#2a1a2f" stroke="none" />
    <circle cx="73" cy="80" r="4" fill="#2a1a2f" stroke="none" />
    <circle cx="71" cy="60" r="3" fill="#2a1a2f" stroke="none" />
    <circle cx="49" cy="86" r="3.4" fill="#2a1a2f" stroke="none" />
  </g>
</svg>
<div class="weavy-beetle-title">Wasm panicked</div>
<div class="weavy-beetle-subtitle">Details are in the console &middot; click to retry</div>
`;

function injectStyles() {
  if (document.getElementById(STYLE_ID) !== null) {
    return;
  }
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = STYLES;
  document.head.appendChild(style);
}

export class DevPanicOverlay {
  private element: HTMLDivElement | undefined = undefined;
  private onRetry: (() => void) | undefined = undefined;

  show(onRetry: () => void) {
    if (this.element !== undefined) {
      this.onRetry = onRetry;
      return;
    }
    injectStyles();
    const element = document.createElement('div');
    element.id = ELEMENT_ID;
    element.setAttribute('role', 'alert');
    // Static beetle SVG + copy only — no user-controlled content.
    element.innerHTML = MARKUP; // nosemgrep: figma.insecure-document-method
    element.addEventListener('click', this.handleClick);
    document.body.appendChild(element);
    this.element = element;
    this.onRetry = onRetry;
  }

  hide() {
    this.element?.removeEventListener('click', this.handleClick);
    this.element?.remove();
    this.element = undefined;
    this.onRetry = undefined;
  }

  private handleClick = () => {
    this.onRetry?.();
  };
}
